/**
 * CreateKartotekaViewModal — Etap 3 T3.1: tworzenie nowego layoutu formularza w bibliotece
 * widoków kartoteki (`kartoteka_views`, viewType='form') — systemowej lub Kreatora (Faza 2).
 *
 * POST /api/admin/kartoteki/{system|creator}/{key}/views. Świeży widok startuje z pustym układem
 * (`{ elements: [] }`) — seed zakładką „Dane" z sekcją WSZYSTKICH pól kartoteki dzieje się przy
 * pierwszym otwarciu embedded buildera (`KartotekaViewFormBuilder`), nie tutaj.
 *
 * Slug generowany z nazwy (wzorzec `AddKartotekaFieldModal`/`CreateKartotekaInline`).
 * Unikalność sprawdzana KLIENCKO przeciw WSZYSTKIM widokom kartoteki (formularze I listy
 * dzielą jedną przestrzeń slugów w bazie — `kartoteka_views` unique po (kind,key,slug) bez
 * względu na viewType): backend robi UPSERT, więc POST na zajęty slug po cichu NADPISZE
 * istniejący widok zamiast zwrócić błąd. Kolizja blokuje submit + inline hint.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import {
  createKartotekaLibraryView,
  type KartotekaIdentityRef,
  kartotekaLibraryViewsQueryKey,
} from '@/lib/kartoteki/kartoteka-definition';

const SLUG_REGEX = /^[a-z][a-z0-9_]*$/;
/** Eksportowane — `KartotekaFormViewsPanel.uniqueSlug` re-używa limitu przy doklejaniu sufiksu. */
export const SLUG_MAX_LENGTH = 50;

/** Lustro `nameToSlug` z `AddKartotekaFieldModal.tsx` — sam wzorzec, inny limit długości. */
export function nameToKartotekaViewSlug(name: string): string {
  return (
    name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, SLUG_MAX_LENGTH) || 'layout'
  );
}

interface CreateKartotekaViewModalProps {
  open: boolean;
  onClose: () => void;
  /** Tożsamość kartoteki (Faza 2): systemowa (slug) lub Kreatora (UUID encji). */
  identity: KartotekaIdentityRef;
  /** Slugi WSZYSTKICH istniejących widoków (form + list) — kolizja = upsert nadpisze cicho. */
  existingSlugs: string[];
  /**
   * Nazwy istniejących widoków TEGO SAMEGO typu (form albo list — osobne przestrzenie nazw,
   * decyzja 2026-08-04). Kliencka podpowiedź „nazwa zajęta"; backend ma ostatnie słowo (409).
   */
  existingNames?: string[];
  /** Typ tworzonego widoku. Domyślnie 'form' (istniejący callsite bez zmian). */
  viewType?: 'form' | 'list';
  /**
   * Config startowy nowego widoku. Domyślnie pusty układ formularza (`{ elements: [] }`).
   * Dla widoku listy podaj domyślny `ListConfig` kartoteki (kolumny walidowane przez backend).
   */
  config?: Record<string, unknown>;
}

export function CreateKartotekaViewModal({
  open,
  onClose,
  identity,
  existingSlugs,
  existingNames = [],
  viewType = 'form',
  config = { elements: [] },
}: CreateKartotekaViewModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const isList = viewType === 'list';
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  // Reset stanu przy każdym otwarciu.
  useEffect(() => {
    if (open) {
      setName('');
      setSlug('');
      setSlugTouched(false);
    }
  }, [open]);

  const effectiveSlug = slugTouched ? slug : nameToKartotekaViewSlug(name);
  const slugValid = SLUG_REGEX.test(effectiveSlug) && effectiveSlug.length <= SLUG_MAX_LENGTH;
  const slugTaken = existingSlugs.includes(effectiveSlug);
  const trimmedNameLower = name.trim().toLowerCase();
  const nameTaken =
    trimmedNameLower.length > 0 &&
    existingNames.some((n) => n.trim().toLowerCase() === trimmedNameLower);

  const createMutation = useMutation({
    mutationFn: () =>
      createKartotekaLibraryView(identity, {
        slug: effectiveSlug,
        name: name.trim(),
        viewType,
        config,
      }),
    onSuccess: async () => {
      toast.show({
        variant: 'success',
        title: isList
          ? t('moduleTemplateEditor.referencePanel.createListViewSuccess')
          : t('moduleTemplateEditor.referencePanel.createViewModal.success'),
      });
      await queryClient.invalidateQueries({
        queryKey: kartotekaLibraryViewsQueryKey(identity),
      });
      onClose();
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data?.error as string | undefined)
        : undefined;
      if (code === 'NAME_TAKEN') {
        toast.show({
          variant: 'error',
          title: t('moduleTemplateEditor.referencePanel.viewNameTaken'),
        });
        return;
      }
      toast.show({
        variant: 'error',
        title: isList
          ? t('moduleTemplateEditor.referencePanel.createListViewError')
          : t('moduleTemplateEditor.referencePanel.createViewModal.error'),
      });
    },
  });

  const canSubmit =
    name.trim().length > 0 && slugValid && !slugTaken && !nameTaken && !createMutation.isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;
    createMutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        isList
          ? t('moduleTemplateEditor.referencePanel.newListViewButton')
          : t('moduleTemplateEditor.referencePanel.createViewModal.title')
      }
      size="md"
    >
      <div className="space-y-4">
        <FormField
          label={t('moduleTemplateEditor.referencePanel.createViewModal.name')}
          hint={nameTaken ? t('moduleTemplateEditor.referencePanel.viewNameTaken') : undefined}
          required
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('moduleTemplateEditor.referencePanel.createViewModal.namePlaceholder')}
            maxLength={100}
            autoFocus
            data-testid="create-kartoteka-view-name"
          />
        </FormField>

        <FormField
          label={t('moduleTemplateEditor.referencePanel.createViewModal.slug')}
          hint={
            slugTaken
              ? t('moduleTemplateEditor.referencePanel.createViewModal.slugTaken')
              : !slugValid
                ? t('moduleTemplateEditor.referencePanel.createViewModal.slugInvalid')
                : undefined
          }
          required
        >
          <Input
            value={effectiveSlug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugTouched(true);
            }}
            maxLength={SLUG_MAX_LENGTH}
            data-testid="create-kartoteka-view-slug"
          />
        </FormField>
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={createMutation.isPending}
          disabled={!canSubmit}
          data-testid="create-kartoteka-view-submit"
        >
          {isList
            ? t('common.create')
            : t('moduleTemplateEditor.referencePanel.createViewModal.submit')}
        </Button>
      </div>
    </Modal>
  );
}
